import { Helmet } from 'react-helmet-async';

interface SchemaMarkupProps {
  type: 'Organization' | 'Service' | 'BreadcrumbList' | 'WebSite';
  data?: Record<string, unknown>;
  breadcrumbs?: Array<{ name: string; url: string }>;
}

const SchemaMarkup = ({ type, data, breadcrumbs }: SchemaMarkupProps) => {
  const siteUrl = 'https://nearsmarter.com';
  
  const getOrganizationSchema = () => ({
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'NearSmarter',
    url: siteUrl,
    logo: `${siteUrl}/logo_normal.webp`,
    description: 'Expert nearshore software teams powered by Luby. Hire vetted developers, designers, and tech experts in 48 hours.',
    foundingDate: '2024',
    parentOrganization: {
      '@type': 'Organization',
      name: 'Luby',
      url: 'https://luby.co'
    },
    sameAs: [
      'https://www.linkedin.com/company/nearsmarter',
      'https://twitter.com/nearsmarter'
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'Customer Service',
      url: `${siteUrl}/contact`
    },
    areaServed: {
      '@type': 'Place',
      name: 'Americas'
    },
    ...data
  });

  const getServiceSchema = (serviceData?: Record<string, unknown>) => ({
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Nearshore Software Development',
    provider: {
      '@type': 'Organization',
      name: 'NearSmarter',
      url: siteUrl
    },
    areaServed: {
      '@type': 'Place',
      name: 'Americas'
    },
    ...serviceData
  });

  const getBreadcrumbSchema = () => {
    if (!breadcrumbs || breadcrumbs.length === 0) return null;
    
    const items = breadcrumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      item: crumb.url.startsWith('http') ? crumb.url : `${siteUrl}${crumb.url}`
    }));

    return {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: items
    };
  };

  const getWebSiteSchema = () => ({
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'NearSmarter',
    url: siteUrl,
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${siteUrl}/search?q={search_term_string}`
      },
      'query-input': 'required name=search_term_string'
    }
  });

  const getSchema = () => {
    switch (type) {
      case 'Organization':
        return getOrganizationSchema();
      case 'Service':
        return getServiceSchema(data);
      case 'BreadcrumbList':
        return getBreadcrumbSchema();
      case 'WebSite':
        return getWebSiteSchema();
      default:
        return null;
    }
  };

  const schema = getSchema();
  
  if (!schema) return null;

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

export default SchemaMarkup;

